import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import useLenis from '../hooks/useLenis';
import api from '../lib/api';

export default function CaseStudyDetail() {
  useLenis();
  const { slug } = useParams();
  const [d, setD] = useState(undefined); // undefined = loading, null = 404, object = found
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    setD(undefined);
    api.get(`/case-studies/${slug}`)
      .then((r) => setD(r.data))
      .catch(() => setD(null));
  }, [slug]);

  if (d === undefined) {
    return (
      <div className="min-h-screen bg-paper text-ink flex items-center justify-center" data-testid="case-loading">
        <div className="mono text-sm text-ink/60">Loading…</div>
      </div>
    );
  }

  if (d === null) {
    return (
      <div className="min-h-screen bg-paper text-ink flex items-center justify-center" data-testid="case-not-found">
        <div className="text-center">
          <div className="serif text-6xl mb-4">404</div>
          <Link to="/" className="mono text-sm underline">← Back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen bg-paper" data-testid={`case-detail-${slug}`}>
      <Nav />
      <main className="pt-32 md:pt-40">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          <Link to="/#work" data-testid="case-back-link" className="mono text-[11px] uppercase tracking-[0.3em] text-ink/60 hover:text-orchid">← All work</Link>
          <div className="mt-6 mono text-xs uppercase tracking-[0.3em] text-orchid">Case · {d.number} — {d.tag}</div>
          <h1 className="serif text-[14vw] md:text-[9vw] leading-[0.9] text-ink mt-4 mb-10">{d.title}</h1>
          <p className="serif italic text-2xl md:text-4xl text-grape leading-tight max-w-3xl mb-16">{d.lede}</p>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-8 space-y-12">
              {d.problem && (
                <section>
                  <h3 className="mono text-[11px] uppercase tracking-[0.3em] text-orchid mb-3">Problem</h3>
                  <p className="mono text-[15px] md:text-[17px] leading-[1.85] text-ink/85 whitespace-pre-wrap">{d.problem}</p>
                </section>
              )}
              {d.approach && (
                <section>
                  <h3 className="mono text-[11px] uppercase tracking-[0.3em] text-orchid mb-3">Approach</h3>
                  <p className="mono text-[15px] md:text-[17px] leading-[1.85] text-ink/85 whitespace-pre-wrap">{d.approach}</p>
                </section>
              )}
              {d.outcome && (
                <section>
                  <h3 className="mono text-[11px] uppercase tracking-[0.3em] text-orchid mb-3">Outcome</h3>
                  <p className="mono text-[15px] md:text-[17px] leading-[1.85] text-ink/85 whitespace-pre-wrap">{d.outcome}</p>
                </section>
              )}
            </div>

            <aside className="md:col-span-4 space-y-8">
              {(d.stack || []).length > 0 && (
                <div className="border border-ink/15 p-6">
                  <div className="mono text-[10px] uppercase tracking-[0.3em] text-orchid mb-3">Stack</div>
                  <ul className="mono text-sm text-ink/80 space-y-1">
                    {d.stack.map((s) => <li key={s}>— {s}</li>)}
                  </ul>
                </div>
              )}
              {d.metric && (
                <div className="border border-ink/15 p-6 bg-lilac/25">
                  <div className="mono text-[10px] uppercase tracking-[0.3em] text-orchid mb-3">The metric</div>
                  <div className="serif text-xl text-ink leading-tight">{d.metric}</div>
                </div>
              )}
              <button
                type="button"
                onClick={() => setContactOpen(true)}
                data-testid="case-want-one-button"
                className="inline-flex items-center gap-2 bg-void text-paper px-6 py-3.5 mono uppercase tracking-[0.22em] text-xs hover:bg-orchid transition-colors"
              >
                Want one? →
              </button>
            </aside>
          </div>

          {(d.images || []).length > 0 && (
            <section className="mt-20 md:mt-28" data-testid="case-gallery">
              <h3 className="mono text-[11px] uppercase tracking-[0.3em] text-orchid mb-6">The work</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {d.images.map((im, i) => (
                  <figure key={i} className="group" data-testid={`case-graphic-${i}`}>
                    <div className="overflow-hidden border border-ink/10 bg-lilac/10">
                      <img
                        src={im.image}
                        alt={im.caption || `${d.title} — graphic ${i + 1}`}
                        loading="lazy"
                        className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                      />
                    </div>
                    {im.caption && (
                      <figcaption className="mono text-[11px] text-ink/55 mt-3">{im.caption}</figcaption>
                    )}
                  </figure>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <div className="mt-32"><Footer /></div>
      <ContactModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        project={d.title}
        title={`Interested in ${d.title}?`}
      />
    </div>
  );
}
